import {
  Component,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type { ErrorInfo, ReactNode } from "react";
import {
  cloneCameraSnapshots,
  createDefaultCameraSnapshots,
} from "./camera-snapshots";
import type { CameraSnapshots } from "./camera-snapshots";
import type { Dimension } from "@src/math/types";
import type { TransformationSceneFactory } from "./three/scene-factory";
import { TransformationViewportRuntime } from "./runtime";
import type { Translate } from "@src/i18n/translate";
import type { ViewportPhase, VisualizationSource } from "./runtime";
import { createTransformationScene } from "./three/scene-factory";

export interface TransformationViewportHandle {
  getCameraSnapshots(): CameraSnapshots;
  restoreCameraSnapshots(snapshots: CameraSnapshots): void;
  resetView(dimension: Dimension): void;
}

interface TransformationViewportProps {
  source: VisualizationSource;
  t: Translate;
  createScene?: TransformationSceneFactory;
  onAvailabilityChange?: (available: boolean) => void;
}

export const TransformationViewport = forwardRef<
  TransformationViewportHandle,
  TransformationViewportProps
>(function TransformationViewport(props, forwardedRef) {
  return (
    <ViewportErrorBoundary source={props.source} t={props.t}>
      <TransformationCanvas {...props} ref={forwardedRef} />
    </ViewportErrorBoundary>
  );
});

const TransformationCanvas = forwardRef<
  TransformationViewportHandle,
  TransformationViewportProps
>(function TransformationCanvas(
  { source, t, createScene = createTransformationScene, onAvailabilityChange },
  forwardedRef,
) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const runtimeRef = useRef<TransformationViewportRuntime | null>(null);
  const cameraSnapshotsRef = useRef<CameraSnapshots>(
    createDefaultCameraSnapshots(),
  );
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [phase, setPhase] = useState<ViewportPhase>({ kind: "preparing" });
  useEffect(() => {
    onAvailabilityChange?.(phase.kind === "rendered");
    return () => {
      if (phase.kind === "rendered") onAvailabilityChange?.(false);
    };
  }, [onAvailabilityChange, phase.kind]);
  const failViewport = useCallback((error: unknown) => {
    console.error("Transformation viewport is unavailable", error);
  }, []);
  const retryViewport = useCallback(() => {
    setPhase({ kind: "preparing" });
    setRetryAttempt((attempt) => attempt + 1);
  }, []);

  useImperativeHandle(
    forwardedRef,
    () => ({
      getCameraSnapshots: () =>
        runtimeRef.current?.getCameraSnapshots() ??
        cloneCameraSnapshots(cameraSnapshotsRef.current),
      restoreCameraSnapshots: (snapshots) => {
        const runtime = runtimeRef.current;
        if (runtime) runtime.restoreCameraSnapshots(snapshots);
        else cameraSnapshotsRef.current = cloneCameraSnapshots(snapshots);
      },
      resetView: (dimension) => {
        const runtime = runtimeRef.current;
        if (runtime) {
          runtime.resetView(dimension);
          return;
        }
        const defaults = createDefaultCameraSnapshots();
        cameraSnapshotsRef.current =
          dimension === 2
            ? { ...cameraSnapshotsRef.current, 2: defaults[2] }
            : { ...cameraSnapshotsRef.current, 3: defaults[3] };
      },
    }),
    [],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const runtime = new TransformationViewportRuntime(
      canvas,
      source,
      cameraSnapshotsRef,
      setPhase,
      failViewport,
      createScene,
    );
    runtimeRef.current = runtime;
    runtime.start();
    return () => {
      if (runtimeRef.current === runtime) runtimeRef.current = null;
      runtime.dispose();
    };
  }, [createScene, failViewport, retryAttempt, source]);

  if (phase.kind === "stopped" && phase.reason === "failed")
    return (
      <ViewportUnavailable source={source} t={t} onRetry={retryViewport} />
    );

  return (
    <>
      <canvas
        ref={canvasRef}
        className="scene-canvas"
        aria-label={t("animatedCoordinateSpace")}
      />
      {phase.kind !== "rendered" && <ViewportPreparing t={t} />}
    </>
  );
});

class ViewportErrorBoundary extends Component<
  {
    children: ReactNode;
    source: VisualizationSource;
    t: Translate;
  },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error("Transformation viewport crashed", error, info);
  }

  private readonly retry = () => {
    this.setState({ failed: false });
  };

  override render() {
    return this.state.failed ? (
      <ViewportUnavailable
        source={this.props.source}
        t={this.props.t}
        onRetry={this.retry}
      />
    ) : (
      this.props.children
    );
  }
}

function ViewportUnavailable({
  source,
  t,
  onRetry,
}: {
  source: VisualizationSource;
  t: Translate;
  onRetry: () => void;
}) {
  useEffect(() => {
    const resetIfPlaying = () => {
      if (source.isAnimating()) source.resetPlayback();
    };
    resetIfPlaying();
    return source.subscribe(resetIfPlaying);
  }, [source]);

  return (
    <div className="scene-unavailable" role="alert" aria-atomic="true">
      <strong>{t("viewportUnavailableTitle")}</strong>
      <span>{t("viewportUnavailableDescription")}</span>
      <button type="button" onClick={onRetry}>
        {t("retryViewport")}
      </button>
    </div>
  );
}

function ViewportPreparing({ t }: { t: Translate }) {
  return (
    <div className="scene-preparing" role="status" aria-live="polite">
      {t("viewportPreparing")}
    </div>
  );
}
